import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/priority/Button/Button'
import { Flex } from '@/components/priority/Flex/Flex'
import { Text } from '@/components/priority/Text/Text'

type SavedLayoutDeleteControlProps = {
  /** Deletes the selected layout; resolves once the drawer has reacted. */
  onDelete: () => Promise<void>
}

/**
 * A customer's delete of a saved layout they created: one click asks, a
 * second deletes, since a deleted layout cannot be brought back.
 */
export const SavedLayoutDeleteControl = ({
  onDelete,
}: SavedLayoutDeleteControlProps) => {
  const { t } = useTranslation()
  const [confirming, setConfirming] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const confirmDelete = async () => {
    setDeleting(true)
    try {
      await onDelete()
    } finally {
      setDeleting(false)
      setConfirming(false)
    }
  }

  if (!confirming)
    return (
      <Flex justify="flex-end" padding="16px 0 0">
        <Button
          destructive
          onClick={() => setConfirming(true)}
          data-testid="saved-layout-delete-button"
        >
          {t('editor.menus.assets.savedLayouts.deleteLayout')}
        </Button>
      </Flex>
    )

  return (
    <Flex column gap="8px" padding="16px 0 0">
      <Text>{t('editor.menus.assets.savedLayouts.deleteLayoutConfirm')}</Text>
      <Flex justify="flex-end" gap="8px">
        <Button
          disabled={deleting}
          onClick={() => setConfirming(false)}
          data-testid="saved-layout-delete-cancel-button"
        >
          {t('editor.menus.assets.savedLayouts.deleteLayoutCancelButton')}
        </Button>
        <Button
          destructive
          disabled={deleting}
          onClick={confirmDelete}
          data-testid="saved-layout-delete-confirm-button"
        >
          {t('editor.menus.assets.savedLayouts.deleteLayoutConfirmButton')}
        </Button>
      </Flex>
    </Flex>
  )
}
